import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './CategorySection.css';

const CategorySection = () => {

  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (headRef.current) observer.observe(headRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.12 }
    );

    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    if (els?.length) {
      els.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, index) => {
      return (
        <span
          key={`${word}-${index}`}
          className="word-animate"
          style={{ animationDelay: `${startDelay + index * 40}ms` }}
        >
          {word}
          {index < words.length - 1 ? ' ' : ''}
        </span>
      );
    });
  };

  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredDevice, setHoveredDevice] = useState(null);



  const categories = [
    {
      id: 1,
      name: 'Student',
      gradient: 'linear-gradient(160deg, #E8F0FE 0%, #C5D9F7 40%, #A8C4F0 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(0,0,0,0.06)"/>
          <rect x="55" y="80" width="90" height="60" rx="4" fill="#2D3748"/>
          <rect x="58" y="83" width="84" height="52" rx="2" fill="#4A90D9"/>
          <rect x="58" y="83" width="84" height="52" rx="2" fill="url(#screenGrad1)"/>
          <rect x="45" y="140" width="110" height="6" rx="3" fill="#2D3748"/>
          <rect x="75" y="146" width="50" height="3" rx="1.5" fill="#4A5568"/>
          <circle cx="100" cy="50" r="18" fill="#F6AD55"/>
          <rect x="88" y="68" width="24" height="30" rx="4" fill="#4299E1"/>
          <path d="M80 42 L100 28 L120 42" stroke="#2D3748" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <rect x="94" y="25" width="12" height="4" rx="2" fill="#2D3748"/>
          <defs>
            <linearGradient id="screenGrad1" x1="58" y1="83" x2="142" y2="135">
              <stop stopColor="#63B3ED"/>
              <stop offset="1" stopColor="#4299E1"/>
            </linearGradient>
          </defs>
        </svg>
      )
    },
    {
      id: 2,
      name: 'Office Use',
      gradient: 'linear-gradient(160deg, #FEF3E8 0%, #FDE1C5 40%, #F9CFA8 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(0,0,0,0.06)"/>
          <rect x="50" y="60" width="100" height="70" rx="5" fill="#2D3748"/>
          <rect x="54" y="64" width="92" height="58" rx="3" fill="#48BB78"/>
          <rect x="60" y="70" width="35" height="6" rx="2" fill="rgba(255,255,255,0.8)"/>
          <rect x="60" y="80" width="50" height="4" rx="2" fill="rgba(255,255,255,0.5)"/>
          <rect x="60" y="88" width="40" height="4" rx="2" fill="rgba(255,255,255,0.5)"/>
          <rect x="60" y="96" width="55" height="4" rx="2" fill="rgba(255,255,255,0.5)"/>
          <rect x="120" y="70" width="22" height="22" rx="3" fill="rgba(255,255,255,0.3)"/>
          <rect x="70" y="130" width="60" height="6" rx="3" fill="#2D3748"/>
          <rect x="85" y="136" width="30" height="30" rx="2" fill="#4A5568"/>
          <circle cx="155" cy="55" r="12" fill="#F6AD55" opacity="0.8"/>
          <rect x="30" y="100" width="15" height="20" rx="2" fill="#E2E8F0"/>
          <rect x="32" y="102" width="11" height="8" rx="1" fill="#CBD5E0"/>
        </svg>
      )
    },
    {
      id: 3,
      name: 'Business',
      gradient: 'linear-gradient(160deg, #E8F8F0 0%, #C5EDDA 40%, #A8E2C4 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(0,0,0,0.06)"/>
          <rect x="60" y="55" width="80" height="55" rx="4" fill="#2D3748"/>
          <rect x="63" y="58" width="74" height="46" rx="2" fill="#4299E1"/>
          <rect x="70" y="65" width="25" height="12" rx="2" fill="rgba(255,255,255,0.3)"/>
          <rect x="100" y="65" width="30" height="12" rx="2" fill="rgba(255,255,255,0.2)"/>
          <rect x="70" y="82" width="60" height="4" rx="2" fill="rgba(255,255,255,0.4)"/>
          <rect x="70" y="90" width="45" height="4" rx="2" fill="rgba(255,255,255,0.3)"/>
          <rect x="50" y="110" width="100" height="5" rx="2.5" fill="#2D3748"/>
          <rect x="80" y="115" width="40" height="3" rx="1.5" fill="#4A5568"/>
          <rect x="130" y="130" width="35" height="35" rx="4" fill="#EDF2F7"/>
          <rect x="134" y="134" width="12" height="12" rx="2" fill="#4299E1" opacity="0.6"/>
          <rect x="149" y="134" width="12" height="12" rx="2" fill="#48BB78" opacity="0.6"/>
          <rect x="134" y="149" width="12" height="12" rx="2" fill="#F6AD55" opacity="0.6"/>
          <rect x="149" y="149" width="12" height="12" rx="2" fill="#FC8181" opacity="0.6"/>
        </svg>
      )
    },
    {
      id: 4,
      name: 'Gaming',
      gradient: 'linear-gradient(160deg, #1A1A2E 0%, #16213E 40%, #0F3460 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(255,255,255,0.08)"/>
          <rect x="45" y="65" width="110" height="70" rx="5" fill="#2D3748"/>
          <rect x="48" y="68" width="104" height="60" rx="3" fill="#1A202C"/>
          <rect x="55" y="75" width="90" height="46" rx="2" fill="#7F5AF0" opacity="0.3"/>
          <path d="M60 100 L80 80 L100 95 L120 75 L140 100" stroke="#00F5D4" strokeWidth="2" fill="none" strokeLinecap="round"/>
          <circle cx="70" cy="90" r="3" fill="#FF6B6B"/>
          <circle cx="130" cy="82" r="3" fill="#00F5D4"/>
          <rect x="55" y="135" width="90" height="8" rx="4" fill="#2D3748"/>
          <rect x="60" y="137" width="12" height="4" rx="2" fill="#7F5AF0"/>
          <rect x="75" y="137" width="12" height="4" rx="2" fill="#00F5D4"/>
          <rect x="90" y="137" width="12" height="4" rx="2" fill="#FF6B6B"/>
          <circle cx="160" cy="55" r="8" fill="#7F5AF0" opacity="0.5"/>
          <circle cx="40" cy="60" r="5" fill="#00F5D4" opacity="0.4"/>
          <path d="M150 150 L165 155 L155 165 Z" fill="#FF6B6B" opacity="0.3"/>
        </svg>
      )
    },
    {
      id: 5,
      name: 'Creative',
      gradient: 'linear-gradient(160deg, #FDE8F0 0%, #F0C5D8 40%, #E8A8C4 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(0,0,0,0.06)"/>
          <rect x="50" y="55" width="100" height="75" rx="5" fill="#2D3748"/>
          <rect x="53" y="58" width="94" height="65" rx="3" fill="#F6F6F6"/>
          <circle cx="80" cy="85" r="12" fill="#FC8181"/>
          <rect x="100" y="70" width="35" height="20" rx="3" fill="#4299E1"/>
          <path d="M60 105 Q80 80 110 100 Q130 115 145 95" stroke="#48BB78" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          <rect x="60" y="110" width="80" height="4" rx="2" fill="#E2E8F0"/>
          <rect x="70" y="130" width="60" height="5" rx="2.5" fill="#2D3748"/>
          <rect x="85" y="135" width="30" height="3" rx="1.5" fill="#4A5568"/>
          <circle cx="40" cy="70" r="8" fill="#F6AD55" opacity="0.5"/>
          <circle cx="165" cy="60" r="6" fill="#FC8181" opacity="0.4"/>
          <rect x="155" cy="140" width="20" height="25" rx="3" fill="#D69E2E" opacity="0.15"/>
          <path d="M160 165 L165 140" stroke="#D69E2E" strokeWidth="2" opacity="0.3"/>
        </svg>
      )
    },
    {
      id: 6,
      name: 'Accessories',
      gradient: 'linear-gradient(160deg, #F0E8FE 0%, #D8C5F7 40%, #C0A8F0 100%)',
      illustration: (
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="cat-illustration">
          <ellipse cx="100" cy="170" rx="60" ry="8" fill="rgba(0,0,0,0.06)"/>
          <rect x="30" y="120" width="80" height="30" rx="5" fill="#2D3748"/>
          <rect x="33" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="44" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="55" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="66" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="77" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="88" y="125" width="8" height="8" rx="2" fill="#4A5568"/>
          <rect x="33" y="136" width="20" height="8" rx="2" fill="#4A5568"/>
          <rect x="56" y="136" width="30" height="8" rx="2" fill="#4A5568"/>
          <rect x="89" y="136" width="15" height="8" rx="2" fill="#4A5568"/>
          <ellipse cx="145" cy="120" rx="20" ry="28" fill="#2D3748"/>
          <ellipse cx="145" cy="120" rx="16" ry="24" fill="#4A5568"/>
          <rect x="125" y="90" width="40" height="8" rx="4" fill="#2D3748"/>
          <path d="M80 60 Q100 40 120 60" stroke="#2D3748" strokeWidth="8" fill="none" strokeLinecap="round"/>
          <circle cx="80" cy="63" r="12" fill="#2D3748"/>
          <circle cx="120" cy="63" r="12" fill="#2D3748"/>
          <circle cx="80" cy="63" r="8" fill="#718096"/>
          <circle cx="120" cy="63" r="8" fill="#718096"/>
        </svg>
      )
    }
  ];

  const deviceTypes = [
    {
      name: 'Laptops',
      image: '/images/types/laptop.webp',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'Portable computing power',
      color: '#667eea'
    },
    {
      name: 'Desktop',
      image: '/images/types/desktop.webp',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      description: 'High-performance desktops',
      color: '#f093fb'
    },
    {
      name: 'Monitors',
      image: '/images/types/monitor.webp',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      description: 'Crystal clear displays',
      color: '#4facfe'
    },
    {
      name: 'Mini PCs',
      image: '/images/types/minipcs.webp',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      description: 'Compact computing',
      color: '#f59e0b'
    },
    {
      name: 'All in One',
      image: '/images/types/allinone.webp',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      description: 'Space-saving elegance',
      color: '#fa709a'
    },
    {
      name: 'Accessories',
      image: '/images/types/accesories.webp',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      description: 'Essential peripherals',
      color: '#a8edea'
    }
  ];

  return (
    <section ref={sectionRef} id="category-section" className="category-section py-16">
      <div className="floating-elements">
        <div className="floating-circle circle-1"></div>
        <div className="floating-circle circle-2"></div>
        <div className="floating-circle circle-3"></div>
      </div>

      <div className="container">
        {/* Device Types Section */}
        <div ref={headRef} className={`section-fade ${isVisible ? 'visible' : ''} flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4`}>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-[1px] bg-teal" />
              <span className="font-mono-label text-[9px] font-medium text-teal uppercase tracking-[0.5em]">
                Device Types
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
              {splitWords('Find your next', 100)}{' '}
              <span className="text-teal inline-block">{splitWords('perfect device.', 220)}</span>
            </h2>
          </div>
          <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
            <span>View All</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        <div className="device-types-grid animate-on-scroll anim-in delay-100">
          {deviceTypes.map((device, index) => (
            <Link
              to={device.name === 'Laptops' ? '/laptops' : device.name === 'Monitors' ? '/monitors' : device.name === 'Desktop' ? '/desktops' : device.name === 'Mini PCs' ? '/mini-pcs' : device.name === 'All in One' ? '/all-in-one' : device.name === 'Accessories' ? '/accessories' : '#'}
              key={index}
              className={`device-type-card ${hoveredDevice === index ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredDevice(index)}
              onMouseLeave={() => setHoveredDevice(null)}
              style={{
                '--device-gradient': device.gradient,
                '--device-text-color': device.color
              }}
            >
              {/* Background SVG pattern */}
              <svg
                className="device-card-background"
                viewBox="0 0 375 283"
                fill="none"
                style={{ opacity: 0.1 }}
              >
                <rect
                  x="159.52"
                  y="175"
                  width="152"
                  height="152"
                  rx="8"
                  transform="rotate(-45 159.52 175)"
                  fill="white"
                />
                <rect
                  y="107.48"
                  width="152"
                  height="152"
                  rx="8"
                  transform="rotate(-45 0 107.48)"
                  fill="white"
                />
              </svg>

              {/* Image container */}
              <div className="device-image-container">
                <div className="device-image-shadow"></div>
                <img
                  className="device-image"
                  src={device.image}
                  alt={device.name}
                />
              </div>

              {/* Content section */}
              <div className="device-content">
                <div className="device-info">
                  <h3>{device.name}</h3>
                  <span className="device-description">
                    {device.description}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex justify-center mt-8 mb-4 sm:hidden">
          <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
            <span>View All</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>



        {/* Categories Section */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4 animate-on-scroll anim-in delay-300">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-[1px] bg-teal" />
              <span className="font-mono-label text-[9px] font-medium text-teal uppercase tracking-[0.5em]">
                Categories
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
              Tailored solutions for{' '}
              <span className="text-teal">every use case.</span>
            </h2>
          </div>
          <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
            <span>View All</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

        <div className="categories-grid">
          {categories.map((category) => (
            <Link
              key={category.id}
              to={category.name === 'Accessories' ? '/accessories' : `/laptops?subcategory=${encodeURIComponent(category.name)}`}
              className={`category-card ${hoveredCategory === category.id ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCategory(category.id)}
              onMouseLeave={() => setHoveredCategory(null)}
              style={{ '--category-gradient': category.gradient, background: category.gradient }}
            >
              <span className="category-title">{category.name}</span>
              <div className="category-illustration">
                {category.illustration}
              </div>
            </Link>
          ))}
        </div>

        <div className="flex justify-center mt-10 sm:hidden">
          <a href="/products" className="view-all-btn" style={{ textDecoration: 'none' }}>
            <span>View All</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>

      </div>
    </section>
  );
};

export default CategorySection;
